import type { CrremDashboardPayload } from "features/crrem-pathway-chart/model/types";

import { Stack } from "@mui/material";
import { DataCard, type DataCardVariant } from "components/DataCard";
import {
  pickDashboardMetric,
  pickScenarioBySettings,
  pickTrendScenarios,
} from "features/crrem-pathway-chart/lib/parseCrremDashboardApi";
import { useTranslation } from "react-i18next";

import type {
  CrremSettings,
  EnergyAnalysisDimension,
  EnergyAnalysisRow,
  EnergyAnalysisViewModel,
} from "../model/types";

import { collectYears, sumValues } from "../lib/aggregate";
import { ENERGY_ANALYSIS_DIMENSION } from "../model/constants";

type Kpi = {
  name: string;
  unit?: string;
  value: number;
  variant?: DataCardVariant;
};

type EnergyAnalysisTabKpisProps = {
  dimension: EnergyAnalysisDimension;
  payload?: CrremDashboardPayload;
  rows: EnergyAnalysisRow[];
  settings?: CrremSettings;
  viewModel: EnergyAnalysisViewModel;
};

const totalAtYear = (rows: EnergyAnalysisRow[], year?: number) =>
  year === undefined ? 0 : sumValues(rows.filter((row) => row.year === year));

const changePercent = (start: number, end: number) => (start === 0 ? 0 : ((end - start) / start) * 100);

const sameCorrections = (
  corrections: { climate: boolean; coverage: boolean; vacancy: boolean },
  expected: { climate: boolean; coverage: boolean; vacancy: boolean }
) =>
  corrections.climate === expected.climate &&
  corrections.coverage === expected.coverage &&
  corrections.vacancy === expected.vacancy;

export const EnergyAnalysisTabKpis = ({
  dimension,
  payload,
  rows,
  settings,
  viewModel,
}: EnergyAnalysisTabKpisProps) => {
  const { t } = useTranslation();
  const snapshotYear = viewModel.referenceYear;
  const years = collectYears(rows);
  const firstYear = years[0];
  let cards: Kpi[];

  if (dimension === ENERGY_ANALYSIS_DIMENSION.joint) {
    const scenario = pickScenarioBySettings(pickTrendScenarios(payload, settings?.trendMethod), settings);
    const energyTotal = totalAtYear(scenario?.energy.rows ?? [], snapshotYear);
    const co2Total = totalAtYear(scenario?.co2.rows ?? [], snapshotYear);
    const firstCo2Total = totalAtYear(scenario?.co2.rows ?? [], firstYear);
    cards = [
      {
        name: t("BuildingProfile.energyAnalysis.kpis.energyForYear", { year: snapshotYear }),
        unit: scenario?.energy.unit || "kWh",
        value: energyTotal,
      },
      {
        name: t("BuildingProfile.energyAnalysis.kpis.co2ForYear", { year: snapshotYear }),
        unit: scenario?.co2.unit || "kgCO₂",
        value: co2Total,
      },
      {
        name: t("BuildingProfile.energyAnalysis.kpis.carbonIntensity", { year: snapshotYear }),
        unit: "CO₂ / energy",
        value: energyTotal === 0 ? 0 : co2Total / energyTotal,
      },
      {
        name: t("BuildingProfile.energyAnalysis.kpis.co2Change", { year: firstYear }),
        unit: "%",
        value: changePercent(firstCo2Total, co2Total),
        variant: co2Total <= firstCo2Total ? "success" : "warning",
      },
    ];
  } else if (dimension === ENERGY_ANALYSIS_DIMENSION.mix) {
    const activeCells = viewModel.mixCells.filter((cell) => cell.value > 0);
    const topCell = [...activeCells].sort((left, right) => right.value - left.value)[0];
    cards = [
      {
        name: t("BuildingProfile.energyAnalysis.kpis.totalForYear", { year: snapshotYear }),
        unit: viewModel.unit,
        value: viewModel.totalValue,
      },
      {
        name: t("BuildingProfile.energyAnalysis.kpis.activeCombinations", { year: snapshotYear }),
        unit: t("BuildingProfile.energyAnalysis.kpis.pairs"),
        value: activeCells.length,
      },
      {
        name: topCell
          ? t("BuildingProfile.energyAnalysis.kpis.topCombination", {
              combination: `${topCell.useTypeLabel} · ${topCell.utilityLabel}`,
            })
          : t("BuildingProfile.energyAnalysis.kpis.topCombinationEmpty"),
        unit: viewModel.unit,
        value: topCell?.value ?? 0,
      },
      {
        name: t("BuildingProfile.energyAnalysis.kpis.topCombinationShare", { year: snapshotYear }),
        unit: "%",
        value: viewModel.totalValue === 0 ? 0 : ((topCell?.value ?? 0) / viewModel.totalValue) * 100,
      },
    ];
  } else if (dimension === ENERGY_ANALYSIS_DIMENSION.corrections) {
    const scenarios = pickTrendScenarios(payload, settings?.trendMethod);
    const baseline = scenarios.find((scenario) =>
      sameCorrections(scenario.corrections, { climate: false, coverage: false, vacancy: false })
    );
    const fullyCorrected = scenarios.find((scenario) =>
      sameCorrections(scenario.corrections, { climate: true, coverage: true, vacancy: true })
    );
    const baselineBlock = baseline && settings ? pickDashboardMetric(baseline, settings.displayMetric) : undefined;
    const correctedBlock =
      fullyCorrected && settings ? pickDashboardMetric(fullyCorrected, settings.displayMetric) : undefined;
    const baselineTotal = totalAtYear(baselineBlock?.rows ?? [], snapshotYear);
    const correctedTotal = totalAtYear(correctedBlock?.rows ?? [], snapshotYear);
    cards = [
      {
        name: t("BuildingProfile.energyAnalysis.kpis.scenarios"),
        unit: t("BuildingProfile.energyAnalysis.kpis.variants"),
        value: scenarios.length,
      },
      {
        name: t("BuildingProfile.energyAnalysis.kpis.measured", { year: snapshotYear }),
        unit: baselineBlock?.unit || viewModel.unit,
        value: baselineTotal,
      },
      {
        name: t("BuildingProfile.energyAnalysis.kpis.fullyCorrected", { year: snapshotYear }),
        unit: correctedBlock?.unit || viewModel.unit,
        value: correctedTotal,
      },
      {
        name: t("BuildingProfile.energyAnalysis.kpis.correctionEffect", { year: snapshotYear }),
        unit: correctedBlock?.unit || viewModel.unit,
        value: correctedTotal - baselineTotal,
        variant: correctedTotal <= baselineTotal ? "success" : "warning",
      },
    ];
  } else {
    const topCategory = [...viewModel.categoryTotals].sort((left, right) => right.value - left.value)[0];
    const startTotal = totalAtYear(rows, firstYear);
    const snapshotTotal = totalAtYear(rows, snapshotYear);
    const categoryLabel =
      dimension === ENERGY_ANALYSIS_DIMENSION.utility
        ? t("BuildingProfile.energyAnalysis.kpis.topUtility")
        : t("BuildingProfile.energyAnalysis.kpis.topUseType");
    cards = [
      {
        name: t("BuildingProfile.energyAnalysis.kpis.totalForYear", { year: snapshotYear }),
        unit: viewModel.unit,
        value: snapshotTotal,
      },
      {
        name: topCategory ? `${categoryLabel}: ${topCategory.label}` : categoryLabel,
        unit: viewModel.unit,
        value: topCategory?.value ?? 0,
      },
      {
        name: t("BuildingProfile.energyAnalysis.kpis.topCategoryShare", { year: snapshotYear }),
        unit: "%",
        value: (topCategory?.share ?? 0) * 100,
      },
      {
        name: t("BuildingProfile.energyAnalysis.kpis.changeSince", { year: firstYear }),
        unit: "%",
        value: changePercent(startTotal, snapshotTotal),
        variant: snapshotTotal <= startTotal ? "success" : "warning",
      },
    ];
  }

  return (
    <Stack className="w-full items-stretch gap-4">
      {cards.map((card) => (
        <DataCard
          className="w-1/4"
          key={card.name}
          kpi={{ name: card.name, unit: card.unit, value: card.value }}
          variant={card.variant}
        />
      ))}
    </Stack>
  );
};
